export const formatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return "";

  try {
    const cleaned = phoneNumber.replace(/\D/g, "");
    const match = cleaned.match(/^(?<areaCode>\d{3})(?<prefix>\d{3})(?<lineNumber>\d{4})$/);

    if (match && match.groups) {
      const { areaCode, prefix, lineNumber } = match.groups;
      return `(${areaCode}) ${prefix}-${lineNumber}`;
    }

    return phoneNumber; // Return original if not a 10-digit number
  } catch (error) {
    console.error("Error formatting phone number:", error);
    return phoneNumber; // Return original on error
  }
};

export const formatCurrency = (amount?: number | string, currency: string = "USD") => {
  if (amount === null || amount === undefined) return "";

  try {
    const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;

    if (isNaN(numericAmount)) {
      console.warn("Invalid currency amount:", amount);
      return amount.toString(); // Return original if not a valid number
    }

    return new Intl.NumberFormat(undefined, {
      maximumSignificantDigits: 6,
      style: "currency",
      currency: currency,
    }).format(numericAmount);
  } catch (error) {
    console.error("Error formatting currency:", error);
    return amount.toString(); // Return original on error
  }
};

export const camelCase = (str: string) => {
  return str.replace(/[-_]+/g, ' ').replace(/(?:^\w|[A-Z]|\b\w)/g, (char, index) => {
    return index === 0 ? char.toLowerCase() : char.toUpperCase();
  }).replace(/[\s+]+/g, "");
};

export const slugify = (str: string) => {
  return str.toLowerCase().replace(/ /g, "-");
};

export const formatDate = (
  dateString: string | undefined,
  format: "US" | "UK" = "US",
  offsetHours: number = 0
): string => {
  if (!dateString) return "";

  try {
    let date = new Date(dateString);
    if (offsetHours !== 0) {
      date = new Date(date.getTime() + offsetHours * 60 * 60 * 1000);
    }
    const locale = format === "UK" ? "en-GB" : "en-US";
    return new Intl.DateTimeFormat(locale, {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  } catch (error) {
    console.error(`Error formatting date "${dateString}":`, error);
    return dateString;
  }
};

export function calculateAspectRatio(width: number, height: number) {
  // Find the greatest common divisor
  const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);

  const divisor = gcd(width, height);
  return {
    width: width / divisor,
    height: height / divisor
  };
}

export const handlePIIFields = (target: HTMLInputElement) => {
  const sanitizedTarget = target.cloneNode() as HTMLInputElement;
  if (
    ["email", "tel"].includes(target.type) ||
    (target.type === "text" && /name|email|phone|zip_code/i.test(target.name))
  ) {
    sanitizedTarget.value = "********";
  }
  return sanitizedTarget;
};


// Swaps the amount inside a display price so derived figures keep the currency
// symbol, suffix, and comma style the API already returned. Pass a string to
// control the decimal places, e.g. `(1402.5).toFixed(2)` for "1,402.50".
export const replaceDisplayAmount = (displayPrice: string, amount: number | string): string => {
  const numberMatch = displayPrice.match(/[\d,]+\.?\d*/);
  if (!numberMatch) return displayPrice;

  const [whole, fraction] = amount.toString().split(".");
  const grouped = numberMatch[0].includes(",") ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : whole;

  return displayPrice.replace(/[\d,]+\.?\d*/, fraction ? `${grouped}.${fraction}` : grouped);
};

export const calculateDiscountedDisplayPrice = (
  displayPrice: string,
  originalPrice: string,
  discount: number
): string =>
  replaceDisplayAmount(displayPrice, Math.round(parseFloat(originalPrice) * (1 - discount / 100)));
